if (!customElements.get('bf-hero')) {
  class BlackFridayHero extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.lifecycle = new AbortController();
      const { signal } = this.lifecycle;
      this.cards = [...this.querySelectorAll('[data-card]')];
      this.stage = this.querySelector('[data-stage]');
      this.shell = this.querySelector('[data-shell]');
      this.controls = this.querySelector('[data-controls]');
      this.pauseButton = this.querySelector('[data-pause]');
      this.countdown = this.querySelector('[data-countdown]');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = Math.max(0, Math.min(Number(this.dataset.initialIndex) || 0, this.cards.length - 1));
      this.visible = true;
      this.hovered = false;
      this.userPaused = false;
      this.editorSelected = false;
      this.suppressClickUntil = 0;
      this.animations = [];
      this.wrapTimers = new Set();
      this.wheelTotal = 0;
      this.wheelDirection = 0;
      this.wheelLockUntil = 0;
      this.pixelCells = [];
      this.pixelWaves = [];
      this.lastPixelWave = 0;

      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); }
      }, { signal });
      this.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, { signal });
      this.addEventListener('focusin', () => this.schedule(), { signal });
      this.addEventListener('focusout', () => {
        queueMicrotask(() => { if (!signal.aborted) this.schedule(); });
      }, { signal });
      this.stage?.addEventListener('pointerdown', (event) => {
        if (event.isPrimary && event.button === 0 && !event.target.closest('a')) {
          this.pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
        }
      }, { signal, passive: true });
      this.stage?.addEventListener('pointerup', (event) => {
        const start = this.pointerStart;
        this.pointerStart = null;
        if (!start || start.id !== event.pointerId) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.3) {
          this.suppressClickUntil = performance.now() + 400;
          this.select(this.index + (dx < 0 ? 1 : -1), true);
        }
      }, { signal, passive: true });
      this.stage?.addEventListener('pointercancel', () => { this.pointerStart = null; }, { signal });
      this.stage?.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      this.stage?.addEventListener('wheel', (event) => this.onWheel(event), { signal, passive: false });
      this.addEventListener('pointermove', (event) => {
        if (!this.pixelContext || this.reducedMotion.matches || this.dataset.animatePixels !== 'true') return;
        const now = Date.now();
        if (now - this.lastPixelWave < 70) return;
        const rect = this.getBoundingClientRect();
        this.pixelWaves.push({ x: event.clientX - rect.left, y: event.clientY - rect.top, start: now });
        if (this.pixelWaves.length > 12) this.pixelWaves.shift();
        this.lastPixelWave = now;
      }, { signal, passive: true });
      document.addEventListener('visibilitychange', () => { this.schedule(); this.schedulePixels(); }, { signal });
      document.addEventListener('shopify:section:load', () => this.syncLink(), { signal });
      document.addEventListener('shopify:section:unload', () => {
        queueMicrotask(() => { if (!signal.aborted) this.syncLink(); });
      }, { signal });
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.cards.findIndex((card) => card.dataset.blockId === event.detail?.blockId || card.contains(event.target));
        if (index < 0) return;
        this.editorSelected = true;
        this.select(index, false, false);
        this.schedule();
      }, { signal });
      this.addEventListener('shopify:block:deselect', () => { this.editorSelected = false; this.schedule(); }, { signal });
      this.reducedMotion.addEventListener('change', () => {
        this.animations.forEach((animation) => animation.cancel());
        this.syncPauseButton();
        this.schedule();
        this.drawPixels();
        this.schedulePixels();
      }, { signal });

      if (this.cards.length) {
        this.classList.add('is-enhanced');
        this.cards.forEach((card) => { card.querySelector('[data-select]').disabled = false; });
        this.shell.hidden = false;
        if (this.controls) this.controls.hidden = this.cards.length < 2;
        this.querySelectorAll('[data-previous], [data-next]').forEach((button) => { button.hidden = this.cards.length < 2; });
        this.select(this.index, false, false);
      }
      this.syncPauseButton();
      this.initCountdown();
      this.classList.add('is-initialized');
      this.initPixels();
      this.resizeObserver = new ResizeObserver(() => { this.fitText(); this.sizePixels(); this.schedulePixels(); });
      this.resizeObserver.observe(this);
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.schedule();
        this.schedulePixels();
      });
      this.visibilityObserver.observe(this);
      document.fonts?.ready.then(() => { if (!signal.aborted) this.fitText(); });
      this.schedule();
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = null;
      clearTimeout(this.autoplayTimer);
      clearInterval(this.countdownTimer);
      clearInterval(this.pixelTimer);
      clearTimeout(this.wheelIdleTimer);
      this.pixelWaves = [];
      this.wrapTimers?.forEach(clearTimeout);
      this.animations?.forEach((animation) => animation.cancel());
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.cards?.forEach((card) => card.classList.remove('is-wrapping'));
      this.pointerStart = null;
    }

    onClick(event) {
      if (performance.now() < this.suppressClickUntil) { event.preventDefault(); return; }
      if (event.target.closest('[data-previous]')) this.select(this.index - 1, true);
      else if (event.target.closest('[data-next]')) this.select(this.index + 1, true);
      else if (event.target.closest('[data-pause]')) {
        this.userPaused = !this.userPaused;
        this.syncPauseButton();
        this.schedule();
      } else if (event.target.closest('[data-select]')) {
        this.select(this.cards.indexOf(event.target.closest('[data-card]')), true);
      } else if (event.target.closest('[data-shell-link]')) {
        const target = this.syncLink();
        if (this.querySelector('[data-shell-link]').getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
        } else if (target) {
          event.preventDefault();
          target.scrollIntoView({ behavior: this.reducedMotion.matches ? 'instant' : 'smooth', block: 'start' });
          history.replaceState(null, '', this.cards[this.index].dataset.link);
        }
      }
    }

    onWheel(event) {
      if (event.ctrlKey || event.metaKey || this.cards.length < 2 || event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      const direction = Math.sign(delta);
      if (!direction) return;
      // Wheel navigation has real ends even though buttons and autoplay can loop.
      if ((direction < 0 && this.index === 0) || (direction > 0 && this.index === this.cards.length - 1)) {
        this.wheelTotal = 0;
        this.wheelLockUntil = 0;
        return;
      }
      event.preventDefault();
      clearTimeout(this.wheelIdleTimer);
      this.wheelIdleTimer = setTimeout(() => { this.wheelTotal = 0; this.wheelDirection = 0; }, 240);
      if (direction !== this.wheelDirection) this.wheelTotal = 0;
      this.wheelDirection = direction;
      if (performance.now() < this.wheelLockUntil) return;
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.stage.clientHeight : 1;
      this.wheelTotal += delta * unit;
      if (Math.abs(this.wheelTotal) < 28) return;
      this.wheelTotal = 0;
      this.wheelLockUntil = performance.now() + (this.reducedMotion.matches ? 200 : 620);
      this.select(this.index + direction, true);
    }

    onKeydown(event) {
      if (!event.target.closest('.bf-hero__carousel') || event.altKey || event.ctrlKey || event.metaKey) return;
      const changes = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.cards.length - 1 };
      if (!(event.key in changes)) return;
      event.preventDefault();
      this.select(changes[event.key], true);
    }

    select(index, announce = false, animate = true) {
      if (!this.cards.length) return;
      const focusWasOnCard = announce && this.contains(document.activeElement) && document.activeElement.matches('[data-select]');
      const previous = this.index;
      this.index = ((index % this.cards.length) + this.cards.length) % this.cards.length;
      this.wrapTimers.forEach(clearTimeout);
      this.wrapTimers.clear();
      this.cards.forEach((card, cardIndex) => {
        let distance = (cardIndex - this.index + this.cards.length) % this.cards.length;
        if (distance > this.cards.length / 2) distance -= this.cards.length;
        const oldDistance = Number(card.style.getPropertyValue('--bf-distance'));
        const wrapping = animate && !this.reducedMotion.matches && Math.abs(oldDistance - distance) > this.cards.length / 2;
        card.classList.toggle('is-wrapping', wrapping);
        card.style.setProperty('--bf-distance', distance);
        card.classList.toggle('is-active', cardIndex === this.index);
        card.querySelector('[data-select]').setAttribute('aria-current', String(cardIndex === this.index));
        card.querySelector('[data-select]').tabIndex = cardIndex === this.index ? 0 : -1;
        if (wrapping) {
          const timer = setTimeout(() => { card.classList.remove('is-wrapping'); this.wrapTimers.delete(timer); }, 640);
          this.wrapTimers.add(timer);
        }
      });
      const current = this.cards[this.index];
      if (focusWasOnCard) current.querySelector('[data-select]').focus({ preventScroll: true });
      this.querySelector('[data-shell-title]').textContent = current.dataset.title;
      this.querySelector('[data-shell-subtitle]').textContent = current.dataset.subtitle;
      const position = this.querySelector('[data-position]');
      if (position) position.textContent = this.index + 1;
      this.fitText();
      this.syncLink();
      this.animations.forEach((animation) => animation.cancel());
      this.animations = [];
      if (animate && previous !== this.index && !this.reducedMotion.matches) {
        const delta = (this.index - previous + this.cards.length) % this.cards.length;
        const direction = delta > this.cards.length / 2 ? -1 : 1;
        this.querySelectorAll('[data-shell-title], [data-shell-subtitle]').forEach((text) => {
          if (text.animate) this.animations.push(text.animate([
            { transform: `translateX(${direction * 105}%)`, opacity: 0 },
            { transform: 'translateX(0)', opacity: 1 }
          ], { duration: 550, easing: 'cubic-bezier(.22,1,.36,1)' }));
        });
      }
      if (announce) this.querySelector('[data-status]').textContent = `${this.index + 1} / ${this.cards.length}: ${current.dataset.title}. ${current.dataset.subtitle}`;
      this.schedule();
    }

    fitText() {
      this.querySelectorAll('[data-shell-title], [data-shell-subtitle]').forEach((text) => {
        text.style.fontSize = '';
        const available = text.parentElement.clientWidth;
        const measured = text.scrollWidth;
        if (available > 0 && measured > available) {
          const base = parseFloat(getComputedStyle(text).fontSize);
          text.style.fontSize = `${Math.max(12, base * available / measured)}px`;
        }
      });
    }

    syncLink() {
      const link = this.querySelector('[data-shell-link]');
      if (!link || !this.cards.length) return null;
      const current = this.cards[this.index];
      const href = current.dataset.link.trim();
      let target = null;
      if (href.startsWith('#')) {
        try { target = document.getElementById(decodeURIComponent(href.slice(1))); } catch { /* Invalid anchor stays unavailable. */ }
      }
      const available = Boolean(href && (!href.startsWith('#') || target));
      link.setAttribute('aria-label', `${link.dataset.label} — ${current.dataset.title}`);
      link.setAttribute('aria-disabled', String(!available));
      if (available) { link.setAttribute('href', href); link.removeAttribute('tabindex'); }
      else { link.removeAttribute('href'); link.setAttribute('tabindex', '-1'); }
      return target;
    }

    syncPauseButton() {
      if (!this.pauseButton) return;
      this.pauseButton.hidden = this.reducedMotion.matches || this.cards.length < 2;
      this.pauseButton.setAttribute('aria-label', this.userPaused ? this.dataset.playLabel : this.dataset.pauseLabel);
      this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
      this.pauseButton.querySelector('[data-pause-icon]').textContent = this.userPaused ? '▶' : 'Ⅱ';
    }

    schedule() {
      clearTimeout(this.autoplayTimer);
      if (!this.lifecycle || this.dataset.autoplay !== 'true' || this.cards.length < 2 || this.userPaused || this.editorSelected || this.hovered || this.contains(document.activeElement) || !this.visible || document.hidden || this.reducedMotion.matches) return;
      this.autoplayTimer = setTimeout(() => this.select(this.index + 1), Math.max(3000, Number(this.dataset.interval) || 3000));
    }

    initCountdown() {
      if (!this.countdown) return;
      const value = this.countdown.dataset.end.trim();
      const isoDate = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:Z|[+-]\d{2}:\d{2})$/.exec(value);
      this.deadline = Date.parse(value);
      const validCalendar = isoDate && Number(isoDate[2]) >= 1 && Number(isoDate[2]) <= 12 && Number(isoDate[3]) >= 1 && Number(isoDate[3]) <= new Date(Date.UTC(Number(isoDate[1]), Number(isoDate[2]), 0)).getUTCDate() && Number(isoDate[4]) < 24 && Number(isoDate[5]) < 60 && Number(isoDate[6] || 0) < 60;
      if (!validCalendar || !Number.isFinite(this.deadline)) {
        this.countdown.hidden = true;
        if (window.Shopify?.designMode) {
          const message = this.countdown.querySelector('[data-ended]');
          message.textContent = 'Enter a valid ISO date with timezone, e.g. 2026-11-27T00:00:00-05:00.';
          message.hidden = false;
          this.countdown.querySelector('[role="timer"]').hidden = true;
          this.countdown.hidden = false;
        }
        return;
      }
      if (this.updateCountdown()) this.countdownTimer = setInterval(() => this.updateCountdown(), 1000);
    }

    updateCountdown() {
      const remaining = Math.max(0, this.deadline - Date.now());
      this.countdown.hidden = false;
      const ended = this.countdown.querySelector('[data-ended]');
      const timer = this.countdown.querySelector('[role="timer"]');
      if (!remaining) {
        clearInterval(this.countdownTimer);
        timer.hidden = true;
        const label = this.countdown.querySelector('.bf-hero__countdown-label');
        if (label) label.hidden = true;
        ended.textContent = this.countdown.dataset.endedText;
        ended.hidden = false;
        this.countdown.hidden = this.countdown.dataset.expiration === 'hide';
        return false;
      }
      timer.hidden = false;
      ended.hidden = true;
      const units = { days: Math.floor(remaining / 86400000), hours: Math.floor(remaining / 3600000) % 24, minutes: Math.floor(remaining / 60000) % 60, seconds: Math.floor(remaining / 1000) % 60 };
      for (const [unit, value] of Object.entries(units)) this.countdown.querySelector(`[data-${unit}]`).textContent = String(value).padStart(2, '0');
      return true;
    }

    initPixels() {
      this.canvas = this.querySelector('.bf-hero__pixels');
      this.pixelContext = this.canvas?.getContext('2d');
      if (!this.pixelContext) return;
      this.sizePixels();
      this.schedulePixels();
    }

    sizePixels() {
      if (!this.pixelContext) return;
      const width = this.clientWidth;
      const height = this.clientHeight;
      if (this.canvas.width === width && this.canvas.height === height) return;
      this.canvas.width = width;
      this.canvas.height = height;
      this.canvas.style.width = `${width}px`;
      this.canvas.style.height = `${height}px`;
      this.pixelColumns = Math.max(1, Math.ceil(width / 11));
      const rows = Math.max(1, Math.ceil(height / 11));
      this.pixelCells = Array.from({ length: this.pixelColumns * rows }, (_, index) =>
        Math.random() < this.pixelChance(index % this.pixelColumns, Math.floor(index / this.pixelColumns), 0) ? 1 : 0);
      this.drawPixels();
    }

    pixelChance(column, row, time) {
      const flow = Math.sin(column * .045 + row * .06 + time * .28) * Math.cos(column * .025 - row * .04 - time * .16);
      const cluster = Math.max(0, (flow - .08) / .92);
      return (.015 + cluster * .55) * .5;
    }

    drawPixels() {
      if (!this.pixelContext) return;
      const { width, height } = this.canvas;
      const context = this.pixelContext;
      const animated = !this.reducedMotion.matches && this.dataset.animatePixels === 'true';
      const now = Date.now();
      const time = animated ? now / 1000 : 0;
      context.clearRect(0, 0, width, height);
      context.fillStyle = '#212121';
      this.pixelWaves = animated ? this.pixelWaves.filter((wave) => now - wave.start < 1500) : [];
      if (animated) {
        const changes = Math.max(1, Math.floor(this.pixelCells.length * .07));
        for (let i = 0; i < changes; i++) {
          const index = Math.floor(Math.random() * this.pixelCells.length);
          this.pixelCells[index] = Math.random() < this.pixelChance(index % this.pixelColumns, Math.floor(index / this.pixelColumns), time) ? 1 : 0;
        }
      }
      this.pixelCells.forEach((cell, index) => {
        const x = (index % this.pixelColumns) * 11;
        const y = Math.floor(index / this.pixelColumns) * 11;
        let ripple = 0;
        this.pixelWaves.forEach((wave) => {
          const age = (now - wave.start) / 1000;
          const distance = Math.hypot(x - wave.x, y - wave.y);
          ripple = Math.max(ripple, Math.max(0, 1 - Math.abs(distance - age * 250) / 65) * (1 - age / 1.5));
        });
        if (cell || (animated && Math.random() < ripple * .375)) context.fillRect(x, y, 4, 4);
      });
    }

    schedulePixels() {
      clearInterval(this.pixelTimer);
      if (!this.lifecycle || !this.pixelContext || !this.visible || document.hidden || this.reducedMotion.matches || this.dataset.animatePixels !== 'true') return;
      this.pixelTimer = setInterval(() => this.drawPixels(), 140);
    }
  }
  customElements.define('bf-hero', BlackFridayHero);
}
